import { createFileRoute } from "@tanstack/react-router";
import { useId, useState } from "react";
import { Gift } from "lucide-react";
import { PriceCard } from "@/components/site/PriceCard";

export const Route = createFileRoute("/prestations")({
  head: () => ({
    meta: [
      { title: "Soins & tarifs — Rénergescence Toulouse" },
      { name: "description", content: "Découvrez les soins proposés : naturopathie (70€), massages (75–110€), magnétisme et soins énergétiques (50–60€). Cabinet à Toulouse." },
      { property: "og:title", content: "Soins & tarifs — Rénergescence" },
      { property: "og:description", content: "Naturopathie, massages, soins énergétiques à Toulouse." },
      { property: "og:url", content: "/prestations" },
    ],
    links: [{ rel: "canonical", href: "https://renergescence.com/prestations" }],
  }),
  component: Prestations,
});

function Badge({ color, children }: { color: string; children: React.ReactNode }) {
  return (
    <span
      className="inline-block rounded-full px-4 py-1 text-xs uppercase tracking-[0.22em]"
      style={{ backgroundColor: color, color: "var(--color-dark)" }}
    >
      {children}
    </span>
  );
}

function Prestations() {
  return (
    <>
      <section className="pt-20 pb-8 md:pt-28 md:pb-12">
        <div className="mx-auto max-w-2xl px-6 text-center">
          <h1 className="font-display text-5xl font-light italic md:text-6xl" style={{ color: "var(--color-sage-text)" }}>Les prestations</h1>
          <p className="mt-6 text-base leading-relaxed md:text-lg" style={{ color: "var(--color-muted-foreground)" }}>
            Chaque soin est adapté selon vos besoins. Je vous invite à me contacter si vous avez des doutes concernant le choix de l'accompagnement qui vous correspond le plus.
          </p>
        </div>
      </section>

      {/* BLOC 1 — NATUROPATHIE */}
      <section id="naturopathie" className="scroll-mt-24 py-16 md:py-20">
        <div className="mx-auto grid max-w-[1100px] gap-12 px-6 md:grid-cols-[1fr_360px] md:gap-16">
          <div>
            <Badge color="var(--color-sage-light)">Santé naturelle</Badge>
            <h2 className="mt-5 font-display text-4xl font-light md:text-5xl">Accompagnements en santé naturelle</h2>
            <p className="mt-3 font-display text-lg italic md:text-xl" style={{ color: "var(--color-sage-text)" }}>
              Naturopathie · Médecine traditionnelle chinoise · Auriculothérapie
            </p>
            <p className="mt-6 text-base leading-relaxed md:text-lg" style={{ color: "var(--color-text)" }}>
              La séance en santé naturelle débute par un bilan complet : habitudes de vie, alimentation, sommeil, antécédents, etc. Des soins ainsi qu'un programme personnalisé sont ensuite mis en place et ajustés au fil des séances.
            </p>
            <ul className="mt-7 space-y-3">
              {[
                "Troubles du sommeil (insomnies, réveils nocturnes, difficultés d'endormissement…)",
                "Accompagnement émotionnel (nervosité, anxiété, états dépressifs, burn out…)",
                "Accompagnement de la femme (troubles menstruels, maternité, post-partum, ménopause…)",
                "Douleurs corporelles (sciatique, rhumatisme, lombalgie, tendinite, contractures musculaires…)",
                "Transition et rupture de vie (deuil, séparation, préparation concours ou compétition…)",
              ].map((m) => (
                <li key={m} className="flex items-start gap-3">
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full" style={{ backgroundColor: "var(--color-sage)" }} />
                  <span style={{ color: "var(--color-text)" }}>{m}</span>
                </li>
              ))}
            </ul>
            <p className="mt-6 text-sm italic" style={{ color: "var(--color-muted-foreground)" }}>
              Les accompagnements s'inscrivent généralement dans un suivi de plusieurs mois.
            </p>
          </div>
          <div>
            <PriceCard title="Séance en santé naturelle" price="70 €" duration="≈ 1h / 1h30" />
          </div>
        </div>
      </section>

      {/* BLOC 2 — MASSAGES */}
      <section id="massages" style={{ backgroundColor: "var(--color-surface)" }} className="scroll-mt-24 py-20 md:py-24">
        <div className="mx-auto max-w-[1100px] px-6">
          <div className="max-w-2xl">
            <Badge color="oklch(0.85 0.06 45)">Massages</Badge>
            <h2 className="mt-5 font-display text-4xl font-light md:text-5xl">Techniques manuelles</h2>
            <p className="mt-6 text-base leading-relaxed md:text-lg" style={{ color: "var(--color-text)" }}>
              Les massages proposés au cabinet allient diverses techniques pour une détente profonde et un travail ciblé. Chaque séance est adaptée à votre état du moment selon votre demande.
            </p>
          </div>
          <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            <PriceCard title="Massage Rénergescence" price="75 €" duration="1h" />
            <PriceCard title="Massage Rénergescence" price="110 €" duration="1h30" />
            <PriceCard title="Massage sportif" price="85 €" duration="1h" />
            <PriceCard title="Massage femme enceinte" price="70 €" duration="1h" />
            <PriceCard title="Soin Essentiel" price="90 €" duration="1h massage + soin énergétique" description="Combinaison massage et soin énergétique pour une relaxation profonde." />
          </div>
        </div>
      </section>

      {/* BLOC 3 — ÉNERGÉTIQUE */}
      <section id="soins-energetiques" className="scroll-mt-24 py-20 md:py-24">
        <div className="mx-auto max-w-[1100px] px-6">
          <div className="max-w-2xl">
            <Badge color="oklch(0.86 0.04 220)">Soins énergétiques</Badge>
            <h2 className="mt-5 font-display text-4xl font-light md:text-5xl">Magnétisme & énergie</h2>
            <p className="mt-6 text-base leading-relaxed md:text-lg" style={{ color: "var(--color-text)" }}>
              Le magnétisme et les soins énergétiques visent à harmoniser l'énergie du corps et de l'esprit. Ils peuvent être pratiqués au cabinet ou à distance, avec la même efficacité.
            </p>
          </div>
          <div className="mt-12 grid gap-5 md:grid-cols-2">
            <PriceCard title="Soin magnétisme" price="50 €" duration="Présentiel ou à distance" />
            <PriceCard title="Soin énergétique global" price="60 €" duration="Présentiel ou à distance" />
          </div>
        </div>
      </section>

      {/* MENTION LÉGALE */}
      <section style={{ backgroundColor: "var(--color-surface)" }} className="py-12">
        <div className="mx-auto max-w-3xl px-6 text-center space-y-4">
          <p className="text-sm italic leading-relaxed" style={{ color: "var(--color-muted-foreground)" }}>
            Les soins proposés par le cabinet Rénergescence sont des pratiques de bien-être complémentaires. Ils ne se substituent en aucun cas à un avis ou à un traitement médical en cours. En cas de symptômes persistants, consultez votre médecin.
          </p>
        </div>
      </section>

      {/* BONS CADEAUX */}
      <BonsCadeauxSection />

      {/* INFOS PRATIQUES */}
      <section className="py-16 md:py-20">
        <div className="mx-auto max-w-3xl px-6">
          <h2 className="text-center font-display text-3xl font-light md:text-4xl">Informations pratiques</h2>
          <ul className="mt-8 space-y-4 text-sm leading-relaxed" style={{ color: "var(--color-text)" }}>
            <li>• Tarifs en vigueur pour l'année 2026.</li>
            <li>• Possibilité d'offrir chacune des prestations sous forme de bon cadeau valable 6 mois.</li>
            <li>• Prise de rendez-vous uniquement par téléphone ou par e-mail. N'hésitez pas à prendre contact pour toute information supplémentaire.</li>
            <li>• Annulation : en cas d'empêchement, merci de décommander votre rendez-vous plus de 48 heures à l'avance. Autrement, la séance sera due.</li>
            <li>• Modes de paiement acceptés : espèces et virement bancaire.</li>
          </ul>
        </div>
      </section>
    </>
  );
}

const giftOptions = [
  { label: "Séance naturopathie — 70 €", value: "Séance naturopathie (70 €)" },
  { label: "Massage Rénergescence 1h — 75 €", value: "Massage Rénergescence 1h (75 €)" },
  { label: "Massage Rénergescence 1h30 — 110 €", value: "Massage Rénergescence 1h30 (110 €)" },
  { label: "Massage sportif 1h — 85 €", value: "Massage sportif (85 €)" },
  { label: "Massage femme enceinte 1h — 70 €", value: "Massage femme enceinte (70 €)" },
  { label: "Soin Essentiel — 90 €", value: "Soin Essentiel (90 €)" },
  { label: "Soin magnétisme — 50 €", value: "Soin magnétisme (50 €)" },
  { label: "Soin énergétique global — 60 €", value: "Soin énergétique global (60 €)" },
];

function BonsCadeauxSection() {
  const [mode, setMode] = useState<"prestation" | "montant">("prestation");
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    const form = e.currentTarget;
    const data = Object.fromEntries(new FormData(form)) as Record<string, string>;
    if (!data.name || !data.email) {
      setError("Merci de remplir votre nom et votre email.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch("/api/public/gift-card-submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: data.name,
          email: data.email,
          beneficiaire: data.beneficiaire || "",
          mode,
          prestation: mode === "prestation" ? data.prestation || "" : "",
          montant: mode === "montant" && data.montant ? Number(data.montant) : null,
          message: data.message || "",
        }),
      });
      if (!res.ok) {
        setSubmitting(false);
        setError("Une erreur est survenue. Merci de réessayer ou de me contacter directement.");
        return;
      }
    } catch {
      setSubmitting(false);
      setError("Une erreur réseau est survenue. Merci de réessayer.");
      return;
    }
    setSubmitting(false);
    form.reset();
    setSent(true);
  };

  return (
    <section id="bons-cadeaux" className="scroll-mt-24 py-20 md:py-24" style={{ backgroundColor: "#F8F5F0" }}>
      <div className="mx-auto max-w-[860px] px-6">
        <div className="text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full" style={{ backgroundColor: "#F0EBE4", color: "#7A8A76" }}>
            <Gift size={26} strokeWidth={1.4} />
          </div>
          <p className="eyebrow mt-5">Pour offrir</p>
          <h2 className="mt-4 font-display text-4xl font-light italic md:text-5xl" style={{ color: "#2D2B28" }}>Bons cadeaux</h2>
          <p className="mx-auto mt-5 max-w-xl text-[15px] leading-relaxed" style={{ color: "#4A4742" }}>
            Offrez une parenthèse de bien-être. Choisissez parmi les prestations disponibles ou un montant libre — bon valable 6 mois.
          </p>
          <p className="mx-auto mt-3 max-w-xl text-[14px] italic" style={{ color: "#6a6258" }}>
            La carte cadeau est envoyée par e-mail sous 24h.
          </p>
        </div>

        <div className="mt-10 rounded-3xl border bg-white p-8 md:p-10" style={{ borderColor: "#e8e0d8" }}>
          {sent ? (
            <div className="py-12 text-center">
              <h3 className="font-display text-3xl italic">Merci !</h3>
              <p className="mt-4" style={{ color: "#4A4742" }}>Votre demande de bon cadeau a bien été envoyée. Je vous recontacte rapidement pour finaliser la commande.</p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-5">
              <div className="flex gap-2">
                <button type="button" onClick={() => setMode("prestation")} className="flex-1 rounded-full border px-5 py-2 text-sm transition-colors" style={{ borderColor: mode === "prestation" ? "#7A8A76" : "#e8e0d8", backgroundColor: mode === "prestation" ? "#7A8A76" : "transparent", color: mode === "prestation" ? "#fff" : "#2D2B28" }}>Une prestation</button>
                <button type="button" onClick={() => setMode("montant")} className="flex-1 rounded-full border px-5 py-2 text-sm transition-colors" style={{ borderColor: mode === "montant" ? "#7A8A76" : "#e8e0d8", backgroundColor: mode === "montant" ? "#7A8A76" : "transparent", color: mode === "montant" ? "#fff" : "#2D2B28" }}>Un montant libre</button>
              </div>
              {mode === "prestation" ? (
              <div>
                <label htmlFor="gift-prestation" className="eyebrow mb-2 block">Prestation à offrir *</label>
                  <select id="gift-prestation" name="prestation" required className="w-full rounded-md border bg-white px-4 py-3 text-sm" style={{ borderColor: "#e8e0d8" }}>
                    {giftOptions.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
              ) : (
                <div>
                  <label htmlFor="gift-montant" className="eyebrow mb-2 block">Montant en € *</label>
                  <input id="gift-montant" name="montant" type="number" min={20} step={5} required placeholder="Ex : 50" className="w-full rounded-md border bg-white px-4 py-3 text-sm" style={{ borderColor: "#e8e0d8" }} />
                </div>
              )}
              <div className="grid gap-5 md:grid-cols-2">
                <GField label="Votre nom *" name="name" required />
                <GField label="Votre email *" name="email" type="email" required />
              </div>
              <GField label="Prénom du bénéficiaire" name="beneficiaire" />
              <div>
                <label htmlFor="gift-message" className="eyebrow mb-2 block">Message personnalisé (optionnel)</label>
                <textarea id="gift-message" name="message" rows={4} className="w-full rounded-md border bg-white px-4 py-3 text-sm" style={{ borderColor: "#e8e0d8" }} />
              </div>
              {error && <p className="text-sm" style={{ color: "var(--color-terra)" }}>{error}</p>}
              <button type="submit" disabled={submitting} className="w-full rounded-full px-6 py-3 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-60" style={{ backgroundColor: "#7A8A76" }}>
                {submitting ? "Envoi en cours…" : "Commander le bon cadeau"}
              </button>
              <p className="text-xs" style={{ color: "#6a6258" }}>Après envoi, je vous recontacte pour confirmer le bon, organiser le règlement (espèces ou virement) et la remise du bon cadeau par e-mail sous 24h.</p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function GField({ label, name, type = "text", required }: { label: string; name: string; type?: string; required?: boolean }) {
  const id = useId();
  return (
    <div>
      <label htmlFor={id} className="eyebrow mb-2 block">{label}</label>
      <input id={id} name={name} type={type} required={required} className="w-full rounded-md border bg-white px-4 py-3 text-sm" style={{ borderColor: "#e8e0d8" }} />
    </div>
  );
}
